import {
  SERPRegionRequestDtoSortByPref,
  SPHotelInfoShort,
  SPHotelPage,
  SPSearchResp,
  TOrgConfig,
} from "@eventr/common";
import { IIpInfoState, ItemOptions } from "@eventr/common-storybook";

export type TFiltersObj = {
  starRating: number;
  priceRange: [number, number];
  userRatingRange: [number, number];
  /** ISO code from the `booking-system` currency area, or an org override of it. */
  currency: string;
};

export type THotelRoom = {
  adults: number;
  children: number[]; /// Ages of each child (under 18)
};

export type TReturnReservationObj = {
  confirm?: string;
  email?: string;
  selectedItinerary?: {
    id: number;
    label: string;
  };
  justReturned: boolean;
};

interface IBookingSliceState {
  bookingLoading: boolean;
  showBookingService: boolean;
  advertData: {
    SPHotelInfoShort: SPHotelInfoShort[];
    timeOfLastReq: number;
  } | null;
  destination: ItemOptions | null;
  dateRange: [string | null, string | null];
  rooms: THotelRoom[];
  sortByPreference: SERPRegionRequestDtoSortByPref;
  filtersObj: TFiltersObj;
  priceBounds: [number, number];
  searchResp: SPSearchResp | null;
  selectedHotel: string | null;
  provider: string | null;
  hotelResp: SPHotelPage | null;
  /** Where `hotelResp`'s rates are: `loaded` means its rate list is the hotel's real availability. */
  hotelRatesStatus: "loading" | "loaded" | "failed";
  eventLonLat?: [number, number];
  returnReservationObj: TReturnReservationObj | null;
}

interface IBookingSliceActions {
  setBookingLoading: (set: boolean) => void;
  setShowBookingService: (orgConfig: TOrgConfig) => void;
  getAdvertData: (location: [number, number]) => Promise<void>;
  setDestination: (destination: ItemOptions | null) => void;
  setDateRange: (dateRange: [string | null, string | null]) => void;
  setRooms: (rooms: THotelRoom[]) => void;
  getDestinationOptions: (search: string, selectedEventInfo: any) => Promise<ItemOptions[]>;
  setSortByPreference: (sortByPreference: SERPRegionRequestDtoSortByPref) => void;
  setFiltersObj: (filterObj: TFiltersObj) => void;
  setPriceBounds: (priceBounds: [number, number]) => void;
  clearFilterObj: () => void;
  setSearchResp: (searchResp: SPSearchResp | null) => void;
  onEnterBookPage: (selectedEventInfo: any, ipInfo: IIpInfoState) => void;
  /** Resolves the booking currency: tag/org default, last-used, IP country, else EUR. */
  syncCurrency: (ipInfo: IIpInfoState) => void;
  setSelectedHotel: (selectedHotel: string | null, provider: string) => void;
  searchHotel: () => void;
  loadMoreHotels: () => Promise<void>;
  initialSearchHotels: (showErrors?: boolean) => Promise<void>;
  searchHotelsWithFilter: () => Promise<void>;
  setReturnReservationObj: (obj: TReturnReservationObj) => void;
  setEventLonLat: (lonlat: [number, number]) => void;
  handleAddReservation: (selectedOrg: string, onSuccess: () => void) => Promise<void>;
}

export const bookingSliceDefaults: IBookingSliceState = {
  bookingLoading: false,
  showBookingService: false,
  advertData: null,
  destination: null,
  dateRange: [null, null],
  rooms: [{ adults: 1, children: [] }],
  sortByPreference: "distance",
  filtersObj: {
    priceRange: [0, 10_000],
    currency: "EUR",
    userRatingRange: [0, 10],
    starRating: 5,
  },
  priceBounds: [0, 10_000],
  searchResp: null,
  selectedHotel: null,
  provider: null,
  hotelResp: null,
  hotelRatesStatus: "loaded",
  returnReservationObj: null,
};

export interface IBookingSlice extends IBookingSliceState, IBookingSliceActions {}
